import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  DragSource,
  DropTarget,
} from 'react-dnd';
import {
  Button,
  Input,
} from 'reactstrap';
import Select from 'react-select';
import { v4 as uuidv4 } from 'uuid';

import { showModal, showModalTop } from '../../../../actions';

import { APIgetPages } from '../../../../api/Pages';

import { thumbnailPlaceholder } from '../../../../helpers/Collections';

import RadioField from './RadioField';
import formatIntl from './formatIntl';

const TARGET_TYPES = ['file', 'collection', 'url', 'page'];

export const normalizePageNavigationItem = (item = {}, index = 0) => {
  const targetType = TARGET_TYPES.includes(item.targetType) ? item.targetType : 'url';

  return {
    id: item.id || uuidv4(),
    title: item.title ?? formatIntl('Navigation item'),
    className: item.className || item.class || '',
    targetType,
    targetCollection: item.targetCollection || null,
    targetFile: item.targetFile || null,
    targetPage: item.targetPage || null,
    url: item.url || '',
    localKey: item.localKey || `${item.id || 'navigation'}-${index}`,
  };
};

const NavigationItemEditor = ({
  item,
  index,
  options,
  onChange,
  onRemove,
  onShowModal,
  onShowModalTop,
  connectDragSource,
  connectDropTarget,
  canReorder,
}) => {
  const dragHandle = canReorder ? connectDragSource(
    <span className="puck-fonts-field-drag-handle mr-2" title={formatIntl('Drag to reorder')}>
      <i className="fas fa-grip-vertical" />
    </span>,
  ) : null;

  const updateItem = (patch) => {
    onChange(index, {
      ...item,
      ...patch,
    });
  };

  const handleCollectionSelect = () => {
    onShowModal('CollectionSelectModal', {
      className: 'modal-collection-select',
      backdrop: true,
      handleCollectionSelect: (selected) => {
        updateItem({ targetCollection: selected || null });
      },
    });
  };

  const handleFileSelect = () => {
    onShowModalTop('FileSelectModalEnhanced', {
      className: 'modal-select-file modal-select-file-enhanced',
      allowUpload: false,
      handleSelectFile: () => {},
      handleFileData: (fileData) => {
        if (!fileData) return;
        updateItem({
          targetFile: {
            uuid: fileData.uuid,
            name: fileData.original_name || fileData.name || '',
            path: fileData.path,
            previewUrl: fileData.urls?.preview,
            thumbnailUrl: fileData.urls?.thumbnail,
          },
        });
      },
    });
  };

  const renderTargetField = () => {
    if (item.targetType === 'collection') {
      return (
        <div className="puck-collection-picker-label">
          {!item.targetCollection && (
            <Button size="sm" color="secondary" onClick={handleCollectionSelect} className="w-100">
              <i className="far fa-folder-open mr-1" />
              {formatIntl('Select Collection')}
            </Button>
          )}
          {item.targetCollection && (
            <div className="puck-feed-collection-item">
              <img
                src={
                  item.targetCollection.featured_file
                    ? item.targetCollection.featured_file.urls.thumbnail
                    : thumbnailPlaceholder
                }
                alt={item.targetCollection.name}
                className="puck-feed-collection-thumb"
              />
              <span className="puck-feed-collection-name">{item.targetCollection.name}</span>
              <Button
                size="sm"
                color="link"
                className="puck-feed-collection-remove"
                onClick={() => updateItem({ targetCollection: null })}
                title={formatIntl('Remove')}
              >
                <i className="far fa-trash mr-0" />
              </Button>
            </div>
          )}
        </div>
      );
    }

    if (item.targetType === 'file') {
      const fileThumbnail = item.targetFile?.previewUrl || item.targetFile?.thumbnailUrl;

      return (
        <div className="puck-file-field-label">
          {fileThumbnail && (
            <div className="puck-file-preview">
              <img
                src={fileThumbnail}
                alt={item.targetFile.name || item.targetFile.uuid}
                className="puck-file-preview-img"
              />
            </div>
          )}
          <div className="puck-file-picker-buttons">
            <Button size="sm" color="secondary" onClick={handleFileSelect} className="w-100 mb-1">
              {item.targetFile
                ? formatIntl('Change File')
                : formatIntl('Select File')}
            </Button>
            {item.targetFile && (
              <Button size="sm" color="secondary" onClick={() => updateItem({ targetFile: null })} className="w-100">
                {formatIntl('Remove')}
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (item.targetType === 'page') {
      const selectedOption = options.find((option) => option.value === item.targetPage?.id) || null;

      return (
        <Select
          isClearable
          options={options}
          value={selectedOption}
          onChange={(selected) => {
            updateItem({
              targetPage: selected ? {
                id: selected.page.id,
                name: selected.page.name,
                url: selected.page.url,
              } : null,
            });
          }}
          placeholder={formatIntl('Select page...')}
          classNamePrefix="page-select"
        />
      );
    }

    return (
      <Input
        type="text"
        bsSize="sm"
        value={item.url || ''}
        onChange={(event) => updateItem({ url: event.target.value })}
        placeholder="https://example.com"
      />
    );
  };

  return connectDropTarget(
    <div className="puck-fonts-field-item">
      <div className="d-flex align-items-center mb-2">
        {dragHandle}
        <Input
          type="text"
          bsSize="sm"
          value={item.title}
          onChange={(event) => updateItem({ title: event.target.value })}
          placeholder={formatIntl('Navigation label')}
        />
        <Button
          size="sm"
          color="link"
          className="ml-2 puck-fonts-field-remove"
          onClick={() => onRemove(index)}
        >
          <i className="far fa-trash" />
        </Button>
      </div>

      <div className="mb-2 mt-2">
        <RadioField
          id={`puck-navigation-target-type-${item.id || index}`}
          name={`puckNavigationTargetType-${item.id || index}`}
          label={formatIntl('Point to')}
          value={item.targetType}
          groupStyle={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          }}
          onChange={(nextTargetType) => {
            updateItem({
              targetType: nextTargetType,
              targetCollection: nextTargetType === 'collection' ? item.targetCollection : null,
              targetFile: nextTargetType === 'file' ? item.targetFile : null,
              targetPage: nextTargetType === 'page' ? item.targetPage : null,
              url: nextTargetType === 'url' ? item.url : '',
            });
          }}
          options={[
            { label: formatIntl('File'), value: 'file' },
            { label: formatIntl('Collection'), value: 'collection' },
            { label: formatIntl('URL'), value: 'url' },
            { label: formatIntl('Page'), value: 'page' },
          ]}
        />
      </div>

      <div className="mb-2">
        {renderTargetField()}
      </div>
    </div>,
  );
};

NavigationItemEditor.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onShowModal: PropTypes.func.isRequired,
  onShowModalTop: PropTypes.func.isRequired,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  canReorder: PropTypes.bool.isRequired,
};

const navigationItemSource = {
  beginDrag(props) {
    return {
      id: props.item.localKey,
      index: props.index,
    };
  },

  canDrag(props) {
    return props.canReorder;
  },
};

const navigationItemTarget = {
  hover(props, monitor) {
    const dragItem = monitor.getItem();
    const dragIndex = dragItem.index;
    const hoverIndex = props.index;

    if (dragItem.id === props.item.localKey || dragIndex === hoverIndex) {
      return;
    }

    props.moveItem(dragIndex, hoverIndex);
    dragItem.index = hoverIndex;
  },
};

const DraggableNavigationItem =
DragSource('puckNavigationItem', navigationItemSource, (DNDconnect) => ({
  connectDragSource: DNDconnect.dragSource(),
}))(DropTarget('puckNavigationItem', navigationItemTarget, (DNDconnect) => ({
  connectDropTarget: DNDconnect.dropTarget(),
}))(NavigationItemEditor));

const mapDispatchToProps = (dispatch) => ({
  onShowModal: (modalName, params) => dispatch(showModal(modalName, params)),
  onShowModalTop: (modalName, params) => dispatch(showModalTop(modalName, params)),
});

const ConnectedDraggableNavigationItem = connect(null, mapDispatchToProps)(DraggableNavigationItem);

const PageNavigationItemsField = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value.map(normalizePageNavigationItem) : [];
  const [pageOptions, setPageOptions] = useState([]);

  useEffect(() => {
    APIgetPages({
      page: 1,
      per_page: 200,
      order_by: 'name',
      order: 'asc',
    })
      .then((response) => {
        const pages = response?.data?.data || [];
        setPageOptions(pages.map((page) => ({
          value: page.id,
          label: page.name || page.url || `#${page.id}`,
          page,
        })));
      })
      .catch(() => {
        setPageOptions([]);
      });
  }, []);

  useEffect(() => {
    const shouldNormalize = items.length !== (Array.isArray(value) ? value.length : 0)
      || items.some(item => !item.localKey);

    if (shouldNormalize) {
      onChange(items);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleItemChange = (index, updatedItem) => {
    onChange(items.map((item, itemIndex) => (itemIndex === index ? updatedItem : item)));
  };

  const handleRemove = (index) => {
    onChange(items.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleAdd = () => {
    onChange([
      ...items,
      normalizePageNavigationItem({
        id: uuidv4(),
        title: formatIntl('New item'),
        targetType: 'url',
        url: '',
      }, items.length),
    ]);
  };

  const moveItem = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) {
      return;
    }

    const nextItems = [...items];
    const [movedItem] = nextItems.splice(fromIndex, 1);
    nextItems.splice(toIndex, 0, movedItem);
    onChange(nextItems);
  };

  const canReorder = items.length > 1;

  return (
    <div className="puck-fonts-field-label">
      {items.map((item, index) => (
        <ConnectedDraggableNavigationItem
          key={item.localKey || item.id || `navigation-item-${index}`}
          item={item}
          index={index}
          options={pageOptions}
          canReorder={canReorder}
          moveItem={moveItem}
          onChange={handleItemChange}
          onRemove={handleRemove}
        />
      ))}

      <Button size="sm" color="primary" onClick={handleAdd} className="w-100">
        <i className="far fa-plus" /> {formatIntl('Add navigation item')}
      </Button>
    </div>
  );
};

PageNavigationItemsField.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

PageNavigationItemsField.defaultProps = {
  value: [],
};

export default PageNavigationItemsField;
